import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import type { SelectChangeEvent } from '@mui/material/Select';
import authService from '../../services/authService';
import auditService from '../../services/auditService';
import showToast from '../../utils/toastUtils';
import { useUploadReceiptCropper } from './useUploadReceiptCropper';
import { normalizeStoresForDropdown } from '../../utils/normalizers/storeNormalizers';
import type { UploadReceiptDialogProps } from '../../types/uploadReceiptDialog';
import type { Store, StoreLocation } from '../../types/services/auth';

export const useUploadReceiptDialogLogic = ({
  open,
  onClose,
  onSuccess,
  stores: providedStores,
  storesLoading,
}: UploadReceiptDialogProps) => {
  const hasProvidedStores = providedStores !== undefined;
  const [fallbackStores, setFallbackStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [locations, setLocations] = useState<StoreLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsResolved, setLocationsResolved] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    editMode,
    modeSwitching,
    perspectiveLoading,
    perspectiveKey,
    horizontalTilt,
    verticalTilt,
    rotation,
    cropperRef,
    perspectiveCropperRef,
    freeCropRef,
    setEditMode,
    setModeSwitching,
    setPerspectiveLoading,
    resetCropperState,
    handlePerspectiveState,
    onCropperChange,
    onRotationChange,
    onRotationDecrease,
    onRotationIncrease,
    handleHorizontalTiltChange,
    onHorizontalTiltDecrease,
    onHorizontalTiltIncrease,
    handleVerticalTiltChange,
    onVerticalTiltDecrease,
    onVerticalTiltIncrease,
    handleFreeCropRevert,
    useOriginalImage,
    startCornerCrop,
    startPerspectiveEdit,
    startRotateEdit,
    getPerspectiveCroppedImg,
    getFreeCroppedImg,
    getCroppedImg,
  } = useUploadReceiptCropper({
    selectedFile,
    previewUrl,
    onError: setError,
  });

  const stores = providedStores ?? fallbackStores;
  const loading = hasProvidedStores ? Boolean(storesLoading) : fallbackLoading;

  const loadStores = useCallback(async () => {
    if (hasProvidedStores) {
      return;
    }

    try {
      setFallbackLoading(true);
      setError(null);
      const storesResp = await authService.getStores({ get_all: true });
      setFallbackStores(normalizeStoresForDropdown(storesResp.stores || []));
    } catch (err) {
      setError('Failed to load stores. Please try again.');
    } finally {
      setFallbackLoading(false);
    }
  }, [hasProvidedStores]);

  const refreshPreviewFromFile = useCallback(
    (file: File) => {
      setSelectedFile(file);
      setPreviewUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return URL.createObjectURL(file);
      });
      resetCropperState({ bumpKey: true });
    },
    [resetCropperState],
  );

  const selectFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPEG, PNG, etc.)');
        showToast.error('Please select an image file (JPEG, PNG, etc.)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        showToast.error('File size must be less than 10MB');
        return;
      }

      setError(null);
      refreshPreviewFromFile(file);
      setEditMode('none');
      setModeSwitching(false);
    },
    [refreshPreviewFromFile, setEditMode, setModeSwitching],
  );

  const handleFileSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        selectFile(file);
      }
    },
    [selectFile],
  );

  const handleStoreChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedStore(event.target.value);
    setLocations([]);
    setSelectedLocation('');
    setLocationsLoading(Boolean(event.target.value));
    setLocationsResolved(false);
    setError(null);
  }, []);

  const handleLocationChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedLocation(event.target.value);
  }, []);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      const file = event.dataTransfer.files[0];
      if (file) {
        selectFile(file);
      }
    },
    [selectFile],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setSelectedStore('');
    setLocations([]);
    setSelectedLocation('');
    setLocationsLoading(false);
    setLocationsResolved(false);
    setPreviewUrl('');
    setError(null);
    setUploading(false);
    setEditMode('none');
    setModeSwitching(false);
    setPerspectiveLoading(false);
    resetCropperState({ bumpKey: false });
    perspectiveCropperRef.current?.backToCrop?.();

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    onClose();
  }, [onClose, previewUrl, perspectiveCropperRef, resetCropperState, setEditMode, setModeSwitching, setPerspectiveLoading]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !selectedStore) {
      setError('Please select both a file and a store');
      showToast.error('Please select both a file and a store');
      return;
    }
    if (locations.length > 1 && !selectedLocation) {
      setError('Please select a store location');
      showToast.error('Please select a store location');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      let fileToUpload: File;
      if (editMode === 'none') {
        fileToUpload = selectedFile;
      } else if (editMode === 'perspective') {
        fileToUpload = await getPerspectiveCroppedImg();
      } else if (editMode === 'free') {
        fileToUpload = await getFreeCroppedImg();
      } else {
        fileToUpload = await getCroppedImg();
      }
      const response = await auditService.uploadReceipt(fileToUpload, selectedStore, {
        storeLocationId: selectedLocation || null,
      });

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      showToast.success('Receipt uploaded successfully!');
      onSuccess(response);
      handleClose();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to upload receipt';
      setError(errorMsg);
      showToast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  }, [
    editMode,
    getCroppedImg,
    getFreeCroppedImg,
    getPerspectiveCroppedImg,
    handleClose,
    onSuccess,
    previewUrl,
    selectedFile,
    selectedLocation,
    selectedStore,
    locations.length,
  ]);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (open) {
      loadStores();
    }
  }, [open, loadStores]);

  useEffect(() => {
    if (!open || stores.length === 0) {
      return;
    }

    setSelectedStore((currentStoreId) => {
      const hasCurrentStore = stores.some((store) => store.id === currentStoreId);
      return hasCurrentStore ? currentStoreId : stores[0].id;
    });
  }, [open, stores]);

  useEffect(() => {
    if (!open || !selectedStore) {
      setLocations([]);
      setSelectedLocation('');
      setLocationsLoading(false);
      setLocationsResolved(false);
      return;
    }

    let cancelled = false;
    setLocations([]);
    setSelectedLocation('');
    setLocationsLoading(true);
    setLocationsResolved(false);

    const loadLocations = async () => {
      try {
        const response = await authService.getStoreLocations(selectedStore);
        if (cancelled) return;

        const activeLocations = response.filter((location) => location.active !== false);
        setLocations(activeLocations);
        setSelectedLocation(activeLocations.length === 1 ? activeLocations[0].id : '');
        setLocationsResolved(true);
      } catch {
        if (!cancelled) {
          setLocations([]);
          setSelectedLocation('');
          setError('Failed to load store locations. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLocationsLoading(false);
        }
      }
    };

    void loadLocations();
    return () => {
      cancelled = true;
    };
  }, [open, selectedStore]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return {
    stores,
    selectedStore,
    locations,
    selectedLocation,
    locationsLoading,
    locationsResolved,
    selectedFile,
    previewUrl,
    loading,
    uploading,
    error,
    editMode,
    modeSwitching,
    perspectiveLoading,
    perspectiveKey,
    horizontalTilt,
    verticalTilt,
    rotation,
    cropperRef,
    perspectiveCropperRef,
    freeCropRef,
    fileInputRef,
    handleStoreChange,
    handleLocationChange,
    handleFileSelect,
    handleBrowseClick,
    handleDrop,
    handleDragOver,
    onCropperChange,
    onRotationChange,
    onRotationDecrease,
    onRotationIncrease,
    handleHorizontalTiltChange,
    onHorizontalTiltDecrease,
    onHorizontalTiltIncrease,
    handleVerticalTiltChange,
    onVerticalTiltDecrease,
    onVerticalTiltIncrease,
    handlePerspectiveState,
    useOriginalImage,
    startCornerCrop,
    startPerspectiveEdit,
    startRotateEdit,
    handleFreeCropRevert,
    handleUpload,
    handleClose,
    clearError,
  };
};
